import { Meteor } from 'meteor/meteor';
import { Mongo } from 'meteor/mongo';
import { DeclareMethod } from '/imports/methods';
import { Doc, FileMeta, GetMethodResponse, ListMethodArg, ListMethodResponse, ModelClass, References } from '/imports/types';
import { validateBoolean, validateString } from '/imports/validators';
import { strings } from '/imports/strings';
import { optionsToDict } from '/imports/utils';

export interface ProjectDocument extends Doc {
  projectId: string;
  name: string;
  file: FileMeta;
  type: 'utilityBill' | 'intervalData' | 'helioscope' | 'avoidedCost' | 'epcQuote' | 'pvSystem' | 'otherPvReport' | 'roofWarranty' | 'siteSurvey' | 'other';
  status: 'uploaded' | 'approved';
  approvedAt?: Date;
  approvedByUserId?: string;
}
export type Model = ProjectDocument;

export const ProjectDocuments = new Mongo.Collection<ProjectDocument>('projectDocuments');

type Refs = References<Model>

export const modelClass: ModelClass<Model, Refs> = {
  collection: ProjectDocuments,
  editableFields: ['active', 'projectId', 'name', 'file', 'type',
  ],
  fieldLimits: {
    'name': 256, 'projectId': 64, 'type': 64,
  },
  listMethod: DeclareMethod<ListMethodArg<ProjectDocument>, ListMethodResponse<ProjectDocument, Refs>>('listProjectDocuments'),
  getMethod: DeclareMethod<string, GetMethodResponse<ProjectDocument, Refs>>('getProjectDocument'),
  createMethod: DeclareMethod<Partial<ProjectDocument>, string>('createProjectDocument'),
  updateMethod: DeclareMethod<Partial<ProjectDocument>, string>('updateProjectDocument'),
  deleteMethod: DeclareMethod<string>('deleteProjectDocument'),
  pathPrefix: '/project-documents',
  asString: (document) => document.name || document._id,
  modelName: strings.Documents(),
  pluralName: strings.Documents(),
  searchQuery: (search?: string) => {
    search = search?.trim();
    if (!search?.length) return {};
    return {
      $or: [
        { _id: search },
        { name: new RegExp(search, 'gi') },
      ]
    };
  },
  validate: async (project: Partial<ProjectDocument>) => {
    const errors: { [key: string]: string[] } = {};
    validateBoolean(errors, project, 'active');
    validateString(errors, project, 'projectId', { required: true });
    validateString(errors, project, 'name', { required: true });
    validateString(errors, project, 'type', { required: true });
    validateString(errors, project, 'status', { required: true });
    return errors;
  },
};

export const DocumentTypeOptions = [
  { value: 'intervalData', label: strings.IntervalData() },
  { value: 'helioscope', label: strings.HelioscopeReport() },
  { value: 'siteSurvey', label: strings.SiteSurvey() },
  { value: 'avoidedCost', label: strings.AvoidedCostAnalysis() },
  { value: 'epcQuote', label: strings.EpcQuote() },
  { value: 'utilityBill', label: strings.UtilityBill() },
  { value: 'pvSystem', label: strings.PvSystem() },
  { value: 'otherPvReport', label: strings.OtherPvDesignReport() },
  { value: 'roofWarranty', label: strings.RoofWarranty() },
  { value: 'other', label: strings.Other() },
];

const DocumentTypeDict = optionsToDict(DocumentTypeOptions);

/** Human-readable label for a document's type. */
export const projectDocumentType = (projectDocument: ProjectDocument) => DocumentTypeDict[projectDocument.type] || projectDocument.type;

interface ListRequiredProjectDocumentsResponse {
  intervalDataDocument?: ProjectDocument, helioscopeDocument?: ProjectDocument, avoidedCostDocument?: ProjectDocument,
  siteSurveyDocument?: ProjectDocument, epcQuoteDocument?: ProjectDocument
}
export const listRequiredProjectDocuments = DeclareMethod<{ projectId: string }, ListRequiredProjectDocumentsResponse>('listRequiredProjectDocuments');
export const approveProjectDocument = DeclareMethod<{ documentId: string }, void>('approveProjectDocument');

Meteor.isServer && Meteor.startup(async () => {
  await ProjectDocuments.createIndexAsync({ projectId: 1 });
  await ProjectDocuments.createIndexAsync({ name: 1 });
  await ProjectDocuments.createIndexAsync({ type: 1 });
});
